import { AlgorithmFlow } from "./AlgorithmFlow";
import { PharmacoTable } from "./PharmacoTable";
import { BulletList } from "./common";
import { PILLARS } from "../lib/treatment";

export { PILLARS };

const List = ({ title, items, testid }) => (items?.length ? (
  <div data-testid={testid}>
    <div className="cc-label mb-2">{title}</div>
    <BulletList items={items} />
  </div>
) : null);

const DiagnosisCard = ({ f }) => {
  const d = f.final_diagnosis || {};
  return (
    <div className="border-2 border-[var(--cc-red)] rounded-md bg-card p-6 cc-rise">
      <div className="cc-label text-[var(--cc-red)]">Final diagnosis</div>
      <div className="font-heading text-2xl font-semibold mt-1" data-testid="final-diagnosis">{d.diagnosis}</div>
      <div className="text-sm text-muted-foreground mt-1">{d.icd10 && `ICD-10 ${d.icd10} · `}Confidence: {d.confidence}</div>
      <p className="text-sm mt-3">{d.rationale}</p>
      {f.secondary_diagnoses?.length > 0 && <p className="text-sm mt-2"><b>Secondary:</b> {f.secondary_diagnoses.join("; ")}</p>}
      {f.severity_assessment && <p className="text-sm mt-1"><b>Severity:</b> {f.severity_assessment}</p>}
    </div>
  );
};

const PrognosisCard = ({ p }) => (
  <div className="border rounded-md bg-card p-6" data-testid="prognosis">
    <div className="cc-label mb-1">Prognosis</div>
    <p className="text-sm">{p.summary}</p>
    {p.factors?.length > 0 && <BulletList items={p.factors} className="list-disc pl-5 text-sm mt-2 space-y-0.5" />}
  </div>
);

const Pillar = ({ num, title, testid, children }) => (
  <div className="cc-pillar" data-testid={testid}>
    <div className="flex items-center gap-3 mb-2">
      <span className="cc-pillar-num">{num}</span>
      <span className="font-heading font-semibold">{title}</span>
    </div>
    {children}
  </div>
);

const FourSteps = ({ t }) => (
  <div className="grid md:grid-cols-2 gap-4" data-testid="tx-four-steps">
    {PILLARS.map(([k, title], i) => (
      <Pillar key={k} num={i + 1} title={title} testid={`tx-${k}`}>
        {t[k]?.length ? <BulletList items={t[k]} /> : <div className="text-sm text-muted-foreground">Not required currently</div>}
      </Pillar>
    ))}
  </div>
);

const TreatmentPlan = ({ f }) => {
  const t = f.treatment || {};
  return (
    <div className="border rounded-md bg-card p-6 space-y-6">
      <h2 className="font-heading text-lg font-semibold">Evidence-based treatment</h2>
      <List title="Immediate" items={t.immediate} testid="tx-immediate" />
      <FourSteps t={t} />
      <Pillar num={5} title="Pharmacotherapy" testid="tx-medications">
        {t.medications?.length ? <PharmacoTable meds={t.medications} /> : <div className="text-sm text-muted-foreground">No specific drugs required currently</div>}
      </Pillar>
      <div className="grid md:grid-cols-2 gap-6">
        <List title="Definitive care & organ support" items={t.definitive_care || t.organ_support} testid="tx-definitive" />
        <List title="Nutrition & prophylaxis" items={t.nutrition_and_prophylaxis} testid="tx-prophylaxis" />
      </div>
      {t.disposition && <p className="text-sm"><b>Disposition:</b> {t.disposition}</p>}
      <List title="References" items={f.references} testid="tx-references" />
      <List title="Caveats" items={f.caveats} testid="tx-caveats" />
    </div>
  );
};

export const TreatmentView = ({ f }) => (
  <div className="space-y-6" data-testid="final-result">
    <DiagnosisCard f={f} />
    <AlgorithmFlow algo={f.algorithm} testid="management-algorithm" />
    {f.prognosis?.summary && <PrognosisCard p={f.prognosis} />}
    <TreatmentPlan f={f} />
  </div>
);
